import { ALL_SITES } from '@tempus/trials-types';

import axios from '../axios';
import { TRIALS_NEST_API_ROOT } from '../constants';
import {
  ACTIVE_CARE_PATHWAY_STATUSES,
  ActiveStatus,
  AgentResponseDto,
  BiomarkerResponseDto,
  BuilderTrialsListResponseDto,
  BuilderTrialsQueryParams,
  BuilderTrialResponseDto,
  BuilderArmRulesetResponseDto,
  CreateBuilderTrialReqBody,
  UpdateBuilderArmRulesetReqBody,
  mapArmRulesetToRulesetViewModel,
  mapEvalJobDetailResponse,
  CareGapResponseDto,
  CareGapSiteSettingResponseDto,
  CareGapSiteSettingsPreviewResponseDto,
  CarePathwayResponseDto,
  CarePathwayStageResponseDto,
  CarePathwaySiteResponseDto,
  CreateAgentDto,
  CreateCareGapSiteSettingReqBody,
  CreateCarePathwayReqBody,
  CreatePathwayStageReqBody,
  CreateCarePathwaySiteReqBody,
  CreateNotificationTemplateReqBody,
  CreateRuleReqBody,
  CreateRulesetAgentDto,
  CreateRulesetReqBody,
  DataSourceResponseDto,
  DiseaseStageResponseDto,
  DuplicateRulesetReqBody,
  EvalEngineCohortResponseDto,
  CreatePatientQueryPromptReqBody,
  CreatePatientQueryJobReqBody,
  NotificationTemplateResponseDto,
  PatientQueryJobResponseDto,
  PatientQueryJobsResponseDto,
  PatientQueryDomain,
  PatientQueryPromptResponseDto,
  PatientQueryTriggerType,
  UpdatePatientQueryPromptReqBody,
  EvalJobsResponseDto,
  EvalJobDetailResponseDto,
  RuleOutputFieldResponseDto,
  RuleResponseDto,
  RulesetAgentResponseDto,
  RulesetOutputElements,
  RulesetResponseDto,
  RulesetValidationResult,
  SetRuleOutputFieldsDto,
  SimulateRulesetReqBody,
  SimulateRulesetResponseDto,
  SubmitEvaluationReqBody,
  SubmitArmRulesetEvaluationReqBody,
  SubmitArmRulesetEvaluationResponseDto,
  SubmitEvaluationResponseDto,
  UpdateAgentDto,
  UpdateCareGapReqBody,
  UpdateCareGapSiteSettingReqBody,
  UpdateCarePathwayReqBody,
  UpdateCarePathwaySiteReqBody,
  UpdatePathwayStageReqBody,
  UpdateNotificationTemplateReqBody,
  UpdateRuleReqBody,
  UpdateRulesetAgentDto,
  UpdateRulesetReqBody,
} from '../types/builder';

export const builderApi = {
  carePathways: {
    list: async (siteId?: string): Promise<CarePathwayResponseDto[]> => {
      const params: { siteId?: string; status: ActiveStatus[] } = { status: ACTIVE_CARE_PATHWAY_STATUSES };
      const trimmedSiteId = siteId?.trim();
      if (trimmedSiteId && trimmedSiteId !== ALL_SITES) {
        params.siteId = trimmedSiteId;
      }
      return axios.get(`${TRIALS_NEST_API_ROOT}/care-pathways`, { params }).then(({ data }) => data);
    },
    get: async (id: string): Promise<CarePathwayResponseDto> => {
      return axios.get(`${TRIALS_NEST_API_ROOT}/care-pathways/${id}`).then(({ data }) => data);
    },
    create: async (body: CreateCarePathwayReqBody): Promise<CarePathwayResponseDto> => {
      return axios.post(`${TRIALS_NEST_API_ROOT}/care-pathways`, body).then(({ data }) => data);
    },
    update: async (id: string, body: UpdateCarePathwayReqBody): Promise<CarePathwayResponseDto> => {
      return axios
        .patch(`${TRIALS_NEST_API_ROOT}/care-pathways/${id}`, body, { params: { _t: Date.now() } })
        .then(({ data }) => data);
    },
    delete: async (id: string): Promise<void> => {
      await axios.delete(`${TRIALS_NEST_API_ROOT}/care-pathways/${id}`, {
        params: { _t: Date.now() },
      });
    },
  },
  carePathwayStages: {
    list: async (carePathwayId: string): Promise<CarePathwayStageResponseDto[]> =>
      axios.get(`${TRIALS_NEST_API_ROOT}/care-pathways/${carePathwayId}/stages`).then(({ data }) => data),
    create: async (carePathwayId: string, body: CreatePathwayStageReqBody): Promise<CarePathwayStageResponseDto> =>
      axios.post(`${TRIALS_NEST_API_ROOT}/care-pathways/${carePathwayId}/stages`, body).then(({ data }) => data),
    update: async (
      carePathwayId: string,
      stageId: string,
      body: UpdatePathwayStageReqBody,
    ): Promise<CarePathwayStageResponseDto> =>
      axios
        .patch(`${TRIALS_NEST_API_ROOT}/care-pathways/${carePathwayId}/stages/${stageId}`, body, {
          params: { _t: Date.now() },
        })
        .then(({ data }) => data),
  },
  trials: {
    list: async (params: BuilderTrialsQueryParams): Promise<BuilderTrialsListResponseDto> => {
      return axios.get(`${TRIALS_NEST_API_ROOT}/builder-trials`, { params }).then(({ data }) => data);
    },
    create: async (body: CreateBuilderTrialReqBody): Promise<BuilderTrialResponseDto> => {
      return axios.post(`${TRIALS_NEST_API_ROOT}/builder-trials`, body).then(({ data }) => data);
    },
    get: async (trialId: string): Promise<BuilderTrialResponseDto> => {
      return axios.get(`${TRIALS_NEST_API_ROOT}/builder-trials/${trialId}`).then(({ data }) => data);
    },
    listArmRulesets: async (trialId: string): Promise<RulesetResponseDto[]> => {
      const data: BuilderArmRulesetResponseDto[] = await axios
        .get(`${TRIALS_NEST_API_ROOT}/builder-trials/${trialId}/arm-rulesets`)
        .then(({ data: responseData }) => responseData);
      return data.map(mapArmRulesetToRulesetViewModel);
    },
    getArmRuleset: async (rulesetId: string): Promise<RulesetResponseDto> => {
      const data: BuilderArmRulesetResponseDto = await axios
        .get(`${TRIALS_NEST_API_ROOT}/builder-trials/arm-rulesets/${rulesetId}`)
        .then(({ data: responseData }) => responseData);
      return mapArmRulesetToRulesetViewModel(data);
    },
    updateArmRuleset: async (rulesetId: string, body: UpdateBuilderArmRulesetReqBody): Promise<RulesetResponseDto> => {
      const data: BuilderArmRulesetResponseDto = await axios
        .patch(`${TRIALS_NEST_API_ROOT}/builder-trials/arm-rulesets/${rulesetId}`, body, { params: { _t: Date.now() } })
        .then(({ data: responseData }) => responseData);
      return mapArmRulesetToRulesetViewModel(data);
    },
    validateArmRuleset: async (rulesetId: string): Promise<RulesetValidationResult> => {
      return axios
        .post(`${TRIALS_NEST_API_ROOT}/builder-trials/arm-rulesets/${rulesetId}/validate`)
        .then(({ data }) => data);
    },
    evaluateArmRuleset: async (
      rulesetId: string,
      body: SubmitArmRulesetEvaluationReqBody,
    ): Promise<SubmitArmRulesetEvaluationResponseDto> => {
      return axios
        .post(`${TRIALS_NEST_API_ROOT}/builder-trials/arm-rulesets/${rulesetId}/evaluate`, body)
        .then(({ data }) => data);
    },
    armRules: {
      list: async (rulesetId: string): Promise<RuleResponseDto[]> => {
        return axios
          .get(`${TRIALS_NEST_API_ROOT}/builder-trials/arm-rulesets/${rulesetId}/rules`, {
            params: { status: 'active' },
          })
          .then(({ data }) => data);
      },
      get: async (rulesetId: string, ruleId: string): Promise<RuleResponseDto> => {
        return axios
          .get(`${TRIALS_NEST_API_ROOT}/builder-trials/arm-rulesets/${rulesetId}/rules/${ruleId}`)
          .then(({ data }) => data);
      },
      create: async (rulesetId: string, body: CreateRuleReqBody): Promise<RuleResponseDto> => {
        return axios
          .post(`${TRIALS_NEST_API_ROOT}/builder-trials/arm-rulesets/${rulesetId}/rules`, body)
          .then(({ data }) => data);
      },
      update: async (rulesetId: string, ruleId: string, body: UpdateRuleReqBody): Promise<RuleResponseDto> => {
        return axios
          .patch(`${TRIALS_NEST_API_ROOT}/builder-trials/arm-rulesets/${rulesetId}/rules/${ruleId}`, body, {
            params: { _t: Date.now() },
          })
          .then(({ data }) => data);
      },
      delete: async (rulesetId: string, ruleId: string): Promise<void> => {
        await axios.delete(`${TRIALS_NEST_API_ROOT}/builder-trials/arm-rulesets/${rulesetId}/rules/${ruleId}`, {
          params: { _t: Date.now() },
        });
      },
    },
  },
  rulesets: {
    list: async (carePathwayId?: string): Promise<RulesetResponseDto[]> => {
      const params = carePathwayId ? { carePathwayId } : undefined;
      return axios.get(`${TRIALS_NEST_API_ROOT}/rulesets`, { params }).then(({ data }) => data);
    },
    get: async (id: string): Promise<RulesetResponseDto> => {
      return axios.get(`${TRIALS_NEST_API_ROOT}/rulesets/${id}`).then(({ data }) => data);
    },
    create: async (body: CreateRulesetReqBody): Promise<RulesetResponseDto> => {
      return axios.post(`${TRIALS_NEST_API_ROOT}/rulesets`, body).then(({ data }) => data);
    },
    update: async (id: string, body: UpdateRulesetReqBody): Promise<RulesetResponseDto> => {
      return axios
        .patch(`${TRIALS_NEST_API_ROOT}/rulesets/${id}`, body, { params: { _t: Date.now() } })
        .then(({ data }) => data);
    },
    duplicate: async (id: string, body: DuplicateRulesetReqBody): Promise<RulesetResponseDto> => {
      return axios.post(`${TRIALS_NEST_API_ROOT}/rulesets/${id}/duplicate`, body).then(({ data }) => data);
    },
    validate: async (id: string): Promise<RulesetValidationResult> => {
      return axios.post(`${TRIALS_NEST_API_ROOT}/rulesets/${id}/validate`).then(({ data }) => data);
    },
    simulate: async (id: string, body: SimulateRulesetReqBody): Promise<SimulateRulesetResponseDto> => {
      return axios.post(`${TRIALS_NEST_API_ROOT}/rulesets/${id}/simulate`, body).then(({ data }) => data);
    },
    delete: async (id: string): Promise<void> => {
      await axios.delete(`${TRIALS_NEST_API_ROOT}/rulesets/${id}`, {
        params: { _t: Date.now() },
      });
    },
  },
  rules: {
    list: async (rulesetId: string): Promise<RuleResponseDto[]> => {
      return axios
        .get(`${TRIALS_NEST_API_ROOT}/rulesets/${rulesetId}/rules`, { params: { status: 'active' } })
        .then(({ data }) => data);
    },
    get: async (rulesetId: string, ruleId: string): Promise<RuleResponseDto> => {
      return axios.get(`${TRIALS_NEST_API_ROOT}/rulesets/${rulesetId}/rules/${ruleId}`).then(({ data }) => data);
    },
    create: async (rulesetId: string, body: CreateRuleReqBody): Promise<RuleResponseDto> => {
      return axios.post(`${TRIALS_NEST_API_ROOT}/rulesets/${rulesetId}/rules`, body).then(({ data }) => data);
    },
    update: async (rulesetId: string, ruleId: string, body: UpdateRuleReqBody): Promise<RuleResponseDto> => {
      return axios
        .patch(`${TRIALS_NEST_API_ROOT}/rulesets/${rulesetId}/rules/${ruleId}`, body, { params: { _t: Date.now() } })
        .then(({ data }) => data);
    },
    delete: async (rulesetId: string, ruleId: string): Promise<void> => {
      await axios.delete(`${TRIALS_NEST_API_ROOT}/rulesets/${rulesetId}/rules/${ruleId}`, {
        params: { _t: Date.now() },
      });
    },
  },
  cohorts: {
    list: async (): Promise<EvalEngineCohortResponseDto[]> => {
      return axios.get(`${TRIALS_NEST_API_ROOT}/rulesets/cohorts`).then(({ data }) => data);
    },
  },
  patientQueryJobs: {
    list: async (
      domain: PatientQueryDomain,
      rulesetId: string,
      triggerType: PatientQueryTriggerType = 'builder',
    ): Promise<PatientQueryJobsResponseDto> => {
      return axios
        .get(`${TRIALS_NEST_API_ROOT}/patient-query/${domain}/rulesets/${rulesetId}/jobs`, {
          params: { pageSize: 100, triggerType },
        })
        .then(({ data }) => data);
    },
    create: async (
      domain: PatientQueryDomain,
      rulesetId: string,
      body: CreatePatientQueryJobReqBody,
    ): Promise<PatientQueryJobResponseDto> => {
      return axios
        .post(`${TRIALS_NEST_API_ROOT}/patient-query/${domain}/rulesets/${rulesetId}/jobs`, body)
        .then(({ data }) => data);
    },
    get: async (
      domain: PatientQueryDomain,
      jobId: string,
      page?: number,
      pageSize?: number,
    ): Promise<PatientQueryJobResponseDto> => {
      const params = { page, pageSize };
      return axios
        .get(`${TRIALS_NEST_API_ROOT}/patient-query/${domain}/jobs/${jobId}`, { params })
        .then(({ data }) => data);
    },
    downloadCsv: async (
      domain: PatientQueryDomain,
      jobId: string,
    ): Promise<{ blob: Blob; contentDisposition: string | null }> => {
      const response = await axios.get<Blob>(`${TRIALS_NEST_API_ROOT}/patient-query/${domain}/jobs/${jobId}/csv`, {
        responseType: 'blob',
      });

      return {
        blob: response.data,
        contentDisposition: contentDispositionHeader(response.headers),
      };
    },
  },
  evaluationJobs: {
    list: async (rulesetId: string, startDate?: string): Promise<EvalJobsResponseDto> => {
      const params = startDate ? { startDate } : undefined;
      return axios
        .get(`${TRIALS_NEST_API_ROOT}/rulesets/${rulesetId}/evaluation-jobs`, { params })
        .then(({ data }) => data);
    },
    get: async (rulesetId: string, jobId: string): Promise<EvalJobDetailResponseDto> => {
      return axios
        .get(`${TRIALS_NEST_API_ROOT}/rulesets/${rulesetId}/evaluation-jobs/${jobId}`)
        .then(({ data }) => mapEvalJobDetailResponse(data));
    },
    evaluate: async (body: SubmitEvaluationReqBody): Promise<SubmitEvaluationResponseDto> => {
      return axios.post(`${TRIALS_NEST_API_ROOT}/rulesets/evaluate`, body).then(({ data }) => data);
    },
  },
  biomarkers: {
    list: async (): Promise<BiomarkerResponseDto[]> => {
      return axios.get(`${TRIALS_NEST_API_ROOT}/biomarkers`).then(({ data }) => data);
    },
    get: async (id: string): Promise<BiomarkerResponseDto> => {
      return axios.get(`${TRIALS_NEST_API_ROOT}/biomarkers/${id}`).then(({ data }) => data);
    },
  },
  dataSources: {
    list: async (): Promise<DataSourceResponseDto[]> => {
      return axios.get(`${TRIALS_NEST_API_ROOT}/data-sources`).then(({ data }) => data);
    },
  },
  diseaseStages: {
    list: async (): Promise<DiseaseStageResponseDto[]> => {
      return axios.get(`${TRIALS_NEST_API_ROOT}/disease-stages`).then(({ data }) => data);
    },
    get: async (id: string): Promise<DiseaseStageResponseDto> => {
      return axios.get(`${TRIALS_NEST_API_ROOT}/disease-stages/${id}`).then(({ data }) => data);
    },
  },
  agents: {
    list: async (): Promise<AgentResponseDto[]> => {
      return axios.get(`${TRIALS_NEST_API_ROOT}/agents`).then(({ data }) => data);
    },
    get: async (id: string): Promise<AgentResponseDto> => {
      return axios.get(`${TRIALS_NEST_API_ROOT}/agents/${id}`).then(({ data }) => data);
    },
    create: async (body: CreateAgentDto): Promise<AgentResponseDto> => {
      return axios.post(`${TRIALS_NEST_API_ROOT}/agents`, body).then(({ data }) => data);
    },
    update: async (id: string, body: UpdateAgentDto): Promise<AgentResponseDto> => {
      return axios
        .patch(`${TRIALS_NEST_API_ROOT}/agents/${id}`, body, { params: { _t: Date.now() } })
        .then(({ data }) => data);
    },
    delete: async (id: string): Promise<void> => {
      await axios.delete(`${TRIALS_NEST_API_ROOT}/agents/${id}`, { params: { _t: Date.now() } });
    },
  },
  rulesetAgents: {
    list: async (rulesetId: string): Promise<RulesetAgentResponseDto[]> => {
      return axios.get(`${TRIALS_NEST_API_ROOT}/rulesets/${rulesetId}/agents`).then(({ data }) => data);
    },
    get: async (rulesetId: string, id: string): Promise<RulesetAgentResponseDto> => {
      return axios.get(`${TRIALS_NEST_API_ROOT}/rulesets/${rulesetId}/agents/${id}`).then(({ data }) => data);
    },
    create: async (rulesetId: string, body: CreateRulesetAgentDto): Promise<RulesetAgentResponseDto> => {
      return axios.post(`${TRIALS_NEST_API_ROOT}/rulesets/${rulesetId}/agents`, body).then(({ data }) => data);
    },
    update: async (rulesetId: string, id: string, body: UpdateRulesetAgentDto): Promise<RulesetAgentResponseDto> => {
      return axios
        .patch(`${TRIALS_NEST_API_ROOT}/rulesets/${rulesetId}/agents/${id}`, body, { params: { _t: Date.now() } })
        .then(({ data }) => data);
    },
    delete: async (rulesetId: string, id: string): Promise<void> => {
      await axios.delete(`${TRIALS_NEST_API_ROOT}/rulesets/${rulesetId}/agents/${id}`, {
        params: { _t: Date.now() },
      });
    },
  },
  patientQueryPrompts: {
    list: async (domain: PatientQueryDomain, rulesetId: string): Promise<PatientQueryPromptResponseDto[]> => {
      return axios
        .get(`${TRIALS_NEST_API_ROOT}/patient-query/${domain}/rulesets/${rulesetId}/prompts`)
        .then(({ data }) => data);
    },
    create: async (
      domain: PatientQueryDomain,
      ruleId: string,
      body: CreatePatientQueryPromptReqBody,
    ): Promise<PatientQueryPromptResponseDto> => {
      return axios
        .post(`${TRIALS_NEST_API_ROOT}/patient-query/${domain}/rules/${ruleId}/prompts`, body)
        .then(({ data }) => data);
    },
    update: async (
      domain: PatientQueryDomain,
      promptId: string,
      body: UpdatePatientQueryPromptReqBody,
    ): Promise<PatientQueryPromptResponseDto> => {
      return axios
        .patch(`${TRIALS_NEST_API_ROOT}/patient-query/${domain}/prompts/${promptId}`, body, {
          params: { _t: Date.now() },
        })
        .then(({ data }) => data);
    },
    delete: async (domain: PatientQueryDomain, promptId: string): Promise<void> => {
      await axios.delete(`${TRIALS_NEST_API_ROOT}/patient-query/${domain}/prompts/${promptId}`, {
        params: { _t: Date.now() },
      });
    },
  },
  ruleOutputFields: {
    list: async (rulesetId: string, ruleId: string): Promise<RuleOutputFieldResponseDto[]> => {
      return axios
        .get(`${TRIALS_NEST_API_ROOT}/rulesets/${rulesetId}/rules/${ruleId}/output-fields`)
        .then(({ data }) => data);
    },
    set: async (
      rulesetId: string,
      ruleId: string,
      body: SetRuleOutputFieldsDto,
    ): Promise<RuleOutputFieldResponseDto[]> => {
      return axios
        .put(`${TRIALS_NEST_API_ROOT}/rulesets/${rulesetId}/rules/${ruleId}/output-fields`, body)
        .then(({ data }) => data);
    },
  },
  careGapSiteSettings: {
    preview: async (): Promise<CareGapSiteSettingsPreviewResponseDto> =>
      axios.get(`${TRIALS_NEST_API_ROOT}/care-gap-site-settings/preview`).then(({ data }) => data),
    create: async (body: CreateCareGapSiteSettingReqBody): Promise<CareGapSiteSettingResponseDto[]> =>
      axios.post(`${TRIALS_NEST_API_ROOT}/care-gap-site-settings`, body).then(({ data }) => data),
    // `siteId` is free-form text (e.g. `001Ph00000RnT23IAF`), not a UUID, so both segments are encoded.
    update: async (
      careGapId: string,
      siteId: string,
      body: UpdateCareGapSiteSettingReqBody,
    ): Promise<CareGapSiteSettingResponseDto> =>
      axios
        .patch(
          `${TRIALS_NEST_API_ROOT}/care-gap-site-settings/${encodeURIComponent(careGapId)}/${encodeURIComponent(siteId)}`,
          body,
          { params: { _t: Date.now() } },
        )
        .then(({ data }) => data),
  },
  carePathwaySites: {
    create: async (body: CreateCarePathwaySiteReqBody): Promise<CarePathwaySiteResponseDto> =>
      axios.post(`${TRIALS_NEST_API_ROOT}/care-pathway-sites`, body).then(({ data }) => data),
    update: async (id: string, body: UpdateCarePathwaySiteReqBody): Promise<CarePathwaySiteResponseDto> =>
      axios
        .patch(`${TRIALS_NEST_API_ROOT}/care-pathway-sites/${id}`, body, { params: { _t: Date.now() } })
        .then(({ data }) => data),
  },
  careGaps: {
    get: async (id: string): Promise<CareGapResponseDto> =>
      axios.get(`${TRIALS_NEST_API_ROOT}/care-gaps/${id}`).then(({ data }) => data),
    update: async (id: string, body: UpdateCareGapReqBody): Promise<CareGapResponseDto> =>
      axios
        .patch(`${TRIALS_NEST_API_ROOT}/care-gaps/${id}`, body, { params: { _t: Date.now() } })
        .then(({ data }) => data),
  },
  notificationTemplates: {
    list: async (): Promise<NotificationTemplateResponseDto[]> =>
      axios.get(`${TRIALS_NEST_API_ROOT}/notification-templates`).then(({ data }) => data),
    create: async (body: CreateNotificationTemplateReqBody): Promise<NotificationTemplateResponseDto> =>
      axios.post(`${TRIALS_NEST_API_ROOT}/notification-templates`, body).then(({ data }) => data),
    update: async (id: string, body: UpdateNotificationTemplateReqBody): Promise<NotificationTemplateResponseDto> =>
      axios
        .patch(`${TRIALS_NEST_API_ROOT}/notification-templates/${id}`, body, { params: { _t: Date.now() } })
        .then(({ data }) => data),
  },
  outputElements: {
    getByAgentType: async (agentType: string): Promise<string[]> => {
      return axios
        .get(`${TRIALS_NEST_API_ROOT}/output-elements/${encodeURIComponent(agentType)}`)
        .then(({ data }) => data);
    },
    getForRuleset: async (rulesetId: string): Promise<RulesetOutputElements> => {
      return axios.get(`${TRIALS_NEST_API_ROOT}/rulesets/${rulesetId}/output-elements`).then(({ data }) => data);
    },
  },
};

function contentDispositionHeader(headers: unknown): string | null {
  if (!headers || typeof headers !== 'object') return null;

  const record = headers as { get?: (name: string) => unknown; [key: string]: unknown };
  const value = typeof record.get === 'function' ? record.get('content-disposition') : record['content-disposition'];

  return typeof value === 'string' && value.length > 0 ? value : null;
}
